import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../../../css/partner/LeadsMainPage.css";
import ToolSidebar from "../../utils/ToolSideBar";

const FILTERS = [
  { value: "open", label: "All Open" },
  { value: "new", label: "Pending" },
  { value: "under_review", label: "Under Review" },
  { value: "more_information_needed", label: "Needs Information" },
  { value: "rejected", label: "Rejected" },
];

function date(value) {
  return value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "-";
}

function projectTypes(lead) {
  return (lead.project_types || []).map((item) => item.name).filter(Boolean).join(", ") || "Not classified";
}

function filterLead(lead, filter) {
  if (filter === "open") return lead.is_open_lead;
  if (filter === "rejected") return lead.is_closed_lead || ["declined", "withdrawn"].includes(lead.review_status);
  return lead.review_status === filter;
}

export default function LeadsMainPage({ leads = [], metrics = {} }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("open");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((lead) => {
      const matchesFilter = filterLead(lead, filter);
      const haystack = [lead.full_name, lead.name, lead.business_name, lead.business, lead.email].filter(Boolean).join(" ").toLowerCase();
      return matchesFilter && (!q || haystack.includes(q));
    });
  }, [leads, query, filter]);

  return (
    <div className="leads-shell">
      <ToolSidebar active="leads" />
      <main className="leads-page">
        <div className="leads-container">
          <header className="leads-header">
            <span>MY LEADS</span>
            <h1>Leads</h1>
            <p>Track new project requests while they move through review.</p>
          </header>

          <section className="lead-metrics" aria-label="Lead metrics">
            <article><span>Total Open Leads</span><strong>{metrics.total_open_leads || 0}</strong></article>
            <article><span>Pending Review</span><strong>{metrics.pending_review || 0}</strong></article>
            <article><span>Needs Information</span><strong>{metrics.needs_information || 0}</strong></article>
            <article><span>Converted Clients</span><strong>{metrics.converted_clients || 0}</strong></article>
          </section>

          <section className="lead-controls">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, business, or email"
              aria-label="Search leads"
            />
            <div className="lead-filter-tabs" role="tablist" aria-label="Lead filters">
              {FILTERS.map((item) => (
                <button key={item.value} type="button" className={filter === item.value ? "active" : ""} onClick={() => setFilter(item.value)}>
                  {item.label}
                </button>
              ))}
            </div>
          </section>

          {filtered.length === 0 ? (
            <div className="leads-empty">
              <p>No leads match this view.</p>
            </div>
          ) : (
            <div className="leads-list">
              {filtered.map((lead) => {
                const detailUrl = lead.is_converted_client && lead.converted_client_id ? `/clients/${lead.converted_client_id}` : `/leads/${lead.id}`;
                return (
                  <article key={lead.id} className={`lead-card ${lead.is_closed_lead ? "closed" : ""}`}>
                    <div className="lead-card-main">
                      <div className="lead-avatar">{(lead.full_name || lead.name || "L").charAt(0).toUpperCase()}</div>
                      <div>
                        <h3>{lead.full_name || lead.name || `Lead #${lead.id}`}</h3>
                        <p>{lead.business_name || lead.business || "Business not provided"}</p>
                      </div>
                    </div>
                    <div className="lead-card-meta">
                      <span className={`lead-status ${lead.review_status}`}>{lead.review_status_label || lead.status_label || "Pending Review"}</span>
                      <span>Submitted {date(lead.created_at)}</span>
                      <span>{projectTypes(lead)}</span>
                      <span>{lead.preferred_contact_method || "Email"}</span>
                    </div>
                    <Link to={detailUrl} className="lead-view-link">View Details</Link>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
